import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatQuotaAmount,
  meteredResourceDefinition,
  type MeteredResource,
  type ResourceUsageView,
} from '@tailoring-crm/shared';

/**
 * A metered allowance, drawn where the resource actually lives.
 *
 * The sibling of `usage-meter.tsx`, which measures SEATS. Seats have a rule no other meter has
 * — an unaccepted invitation is a seat already spent — so the two components stay apart rather
 * than one growing an `if` for it.
 *
 * Two forms, and the difference is where they are read:
 *
 *  - {@link ResourceMeter} is the billing screen's: a labelled bar with a sentence under it,
 *    read once a quarter by somebody deciding what to pay.
 *  - {@link QuotaLine} is the in-module affordance: one quiet line above a list, read by
 *    somebody who came to that screen to do something else entirely. It draws NOTHING until
 *    the meter is four fifths full, because a permanent "3 of 500" above the customer book is
 *    noise that teaches people to stop seeing meters — and the day it says 498 it needs to be
 *    the first thing they notice.
 */

function label(resource: MeteredResource, translate: (key: string) => string): string {
  // The shared definition carries the English default; i18next overrides it where a locale
  // has one, so a new metered resource renders sensibly before anybody writes its string.
  const key = `billing.usage.resource.${resource}`;
  const translated = translate(key);
  return translated === key ? meteredResourceDefinition(resource).defaultLabel : translated;
}

export interface ResourceMeterProps {
  usage: ResourceUsageView;
}

export function ResourceMeter({ usage }: ResourceMeterProps): ReactElement {
  const { t } = useTranslation();
  const name = label(usage.resource, t);
  // Scaled to the allowance, so both halves of "0.8 GB of 1.0 GB" are in the same unit.
  const amount = (value: number): string =>
    formatQuotaAmount(usage.resource, value, usage.allowance ?? value);

  if (usage.allowance === null) {
    return (
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{name}</span>
        <span className="text-sm text-muted-foreground">
          {t('billing.usage.usedUnlimited', { used: amount(usage.used) })}
        </span>
      </div>
    );
  }

  const ratio = (usage.percentUsed ?? 0) / 100;
  const tone = usage.atLimit ? 'bg-destructive' : usage.nearLimit ? 'bg-warning' : 'bg-primary';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{name}</span>
        <span className="text-sm text-muted-foreground">
          {t('billing.usage.count', {
            used: amount(usage.used),
            allowance: amount(usage.allowance),
          })}
        </span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-muted"
        role="meter"
        aria-label={name}
        aria-valuenow={usage.used}
        aria-valuemin={0}
        aria-valuemax={usage.allowance}
      >
        <div
          className={`h-full rounded-full ${tone}`}
          style={{ width: `${String(ratio * 100)}%` }}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        {usage.exceeded
          ? t('billing.usage.overLimit')
          : usage.atLimit
            ? t('billing.usage.resourceFull')
            : t('billing.usage.resourceRemaining', { amount: amount(usage.remaining ?? 0) })}
      </p>
    </div>
  );
}

export interface QuotaLineProps {
  usage: ResourceUsageView | undefined;
  /**
   * Draw the line whatever the reading. Off by default — see the note above on why a meter
   * that is always there is a meter nobody sees.
   */
  always?: boolean;
}

/** The in-module affordance: "Catalogue items 48 of 50" above the list somebody is adding to. */
export function QuotaLine({ usage, always = false }: QuotaLineProps): ReactElement | null {
  const { t } = useTranslation();
  if (!usage || usage.allowance === null) return null;
  if (!always && !usage.nearLimit) return null;

  const name = label(usage.resource, t);
  const amount = (value: number): string =>
    formatQuotaAmount(usage.resource, value, usage.allowance ?? value);
  const tone = usage.atLimit
    ? 'text-destructive'
    : usage.nearLimit
      ? 'text-warning-foreground'
      : 'text-muted-foreground';

  return (
    <p className={`text-sm ${tone}`} role={usage.atLimit ? 'alert' : undefined}>
      {t('billing.usage.inline', {
        label: name,
        used: amount(usage.used),
        allowance: amount(usage.allowance),
      })}
      {usage.atLimit ? ` ${t('billing.usage.inlineFull')}` : ''}
    </p>
  );
}

export default ResourceMeter;
